import React, { DragEvent, useCallback, useRef, useState } from "react";
import { FiArrowLeft, FiDownload, FiPaperclip } from "react-icons/fi";
import { useToast } from "../../../hooks/toast";
import { useAuth } from "../../../hooks/auth";
import api from "../../../services/api";
import refreshToken from "../../../utils/refreshToken";
import { downloadPropertyPhotosZip } from "../../../utils/downloadPropertyPhotosAsZip";
import type { DropzoneInputProps, DropzoneRootProps } from "react-dropzone";
import { formatPropertyPhotoError } from "../../../utils/propertyPhotoUpload";
import ImageBox from "../../../components/ImageBox";
import PhotoAiPreviewCompareCard from "../../../components/PhotoAiPreviewCompareCard";
import type { PhotoAiPreviewItem } from "../../../types/photoAiEdit";
import {
  AIPreviewGrid,
  AIPreviewModalContent,
  AIPreviewModalHeader,
  AIPreviewModalOverlay,
  AIPreviewModalTitle,
  CloseModalButton,
  OpenAIPreviewsButton,
  PhotoContainer,
} from "../twStyles";
import {
  ButtonsWrapper,
  NextButton,
  RegisterButton,
} from "../../CreateProperty/twStyles";
import SaveDraftButton from "../../../components/SaveDraftButton";
import { type EditPropertyStepId } from "./stepConstants";

function cn(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(" ");
}

export interface IPhotos {
  id: number;
  order: number;
  url_l: string;
  url_m: string;
  url_s: string;
}

export interface IImageError {
  name: string;
  reason: string;
}

export interface IImagesWithError {
  name: string;
  reason: string;
}

export interface IStateProps {
  isToShowPhotos: string;
  imagesWithError: IImagesWithError[];
}

export interface EditPhotosStepProps {
  propertyId: number;
  getRootProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  isDragActive: boolean;
  loadingPhotosRequest: boolean;
  state: IStateProps | undefined;
  imageError: IImageError[];
  photos: IPhotos[];
  loadingRequestToRemovePhoto: boolean;
  hadleDragImageBox: (ev: DragEvent<HTMLDivElement>) => void;
  handleDropImageBox: (ev: DragEvent<HTMLDivElement>) => void;
  handleRemovePhoto: (id: number) => void;
  onVirtualStaging: (photoId: number) => void;
  onCustomEdit: (photoId: number) => void;
  generatingAI: Record<number, boolean>;
  aiPreviews: PhotoAiPreviewItem[];
  isAIPreviewModalOpen: boolean;
  setIsAIPreviewModalOpen: (v: boolean) => void;
  handleApplyAI: (preview: PhotoAiPreviewItem) => void;
  handleDiscardAI: (previewId: number) => void;
  handleTryAgainAI: (preview: PhotoAiPreviewItem) => void;
  loadingRequest: boolean;
  handleChangeOrders: () => void;
  setFormOptionSelected: (id: EditPropertyStepId) => void;
  isDraft?: boolean;
  handlePublishDraft?: () => void;
  isSavingDraft?: boolean;
  handleSavePhotosDraft?: () => void | Promise<void>;
}

export default function EditPhotosStep({
  propertyId,
  getRootProps,
  getInputProps,
  isDragActive,
  loadingPhotosRequest,
  imageError,
  photos,
  loadingRequestToRemovePhoto,
  hadleDragImageBox,
  handleDropImageBox,
  handleRemovePhoto,
  onVirtualStaging,
  onCustomEdit,
  generatingAI,
  aiPreviews,
  isAIPreviewModalOpen,
  setIsAIPreviewModalOpen,
  handleApplyAI,
  handleDiscardAI,
  handleTryAgainAI,
  loadingRequest,
  handleChangeOrders,
  setFormOptionSelected,
  isDraft = false,
  handlePublishDraft,
  isSavingDraft = false,
  handleSavePhotosDraft,
}: EditPhotosStepProps) {
  const { addToast } = useToast();
  const { access_token, dateToExpires } = useAuth();
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [zipReceiveBytes, setZipReceiveBytes] = useState<number | null>(null);
  const downloadZipBusyRef = useRef(false);

  const handleDownloadAllPhotos = useCallback(async () => {
    if (photos.length === 0 || downloadZipBusyRef.current) return;
    downloadZipBusyRef.current = true;
    setDownloadingZip(true);
    setZipReceiveBytes(null);
    try {
      await downloadPropertyPhotosZip(
        propertyId,
        {
          api,
          getAccessToken: async () => {
            const t = await refreshToken(dateToExpires, access_token);
            return t ?? access_token;
          },
        },
        {
          onProgress: (loaded, total) => {
            setZipReceiveBytes(loaded);
            if (total != null && total > 0 && loaded >= total) {
              setZipReceiveBytes(total);
            }
          },
        }
      );
      addToast({
        type: "success",
        title: "Download concluído",
        description: `O nome do arquivo segue o servidor (Content-Disposition) ou imovel-${propertyId}-fotos.zip.`,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Tente novamente mais tarde.";
      addToast({
        type: "error",
        title: "Não foi possível baixar as fotos",
        description: message,
      });
    } finally {
      downloadZipBusyRef.current = false;
      setDownloadingZip(false);
      setZipReceiveBytes(null);
    }
  }, [photos, propertyId, addToast, access_token, dateToExpires]);

  const dropRootProps = getRootProps();

  return (
    <form className="mb-[25%] mt-10 w-full rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
      <p className="text-xs font-medium text-slate-500">
        * Você pode reorganizar as fotos arrastando para as novas posições;
        depois clique em salvar.
      </p>
      <p className="mt-1 text-xs font-medium text-slate-500">
        ** Após upload as fotos são salvas automaticamente
      </p>

      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
        <button
          type="button"
          disabled={
            photos.length === 0 ||
            downloadingZip ||
            loadingPhotosRequest
          }
          onClick={() => void handleDownloadAllPhotos()}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiDownload className="h-4 w-4 shrink-0" aria-hidden />
          {downloadingZip
            ? zipReceiveBytes != null && zipReceiveBytes > 0
              ? `Recebendo… ${(zipReceiveBytes / (1024 * 1024)).toFixed(1)} MB`
              : "Recebendo ZIP…"
            : "Baixar todas (ZIP)"}
        </button>
      </div>

      <div
        {...dropRootProps}
        className={cn(
          "mt-4 flex min-h-[128px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition",
          isDragActive
            ? "border-amber-400/70 bg-amber-50/60"
            : "border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-slate-100/80",
          dropRootProps.className ?? ""
        )}
      >
        <input {...getInputProps()} />

        {!loadingPhotosRequest && (
          <>
            {isDragActive ? (
              <span className="text-sm font-medium text-slate-600">
                Solte as imagens aqui
              </span>
            ) : (
              <span className="flex flex-col items-center gap-2 text-sm font-semibold text-slate-700 sm:flex-row">
                <FiPaperclip className="h-6 w-6 text-slate-500" aria-hidden />
                Adicionar fotos
              </span>
            )}
          </>
        )}

        {loadingPhotosRequest && (
          <span className="text-sm font-medium text-slate-600">Carregando…</span>
        )}
      </div>

      {imageError.length >= 1 &&
        imageError.map((image, idx) => (
          <p
            key={`${image.name}-${idx}`}
            className="mt-3 text-sm text-red-600"
          >
            {formatPropertyPhotoError(image)}
          </p>
        ))}

      <PhotoContainer className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {[...photos]
          .sort((a, b) => a.order - b.order)
          .map((photo) => (
            <ImageBox
              key={photo.id}
              photo={photo}
              boxNumber={photo.id}
              isLoading={loadingRequestToRemovePhoto}
              handleDrag={hadleDragImageBox}
              handleDrop={handleDropImageBox}
              handleRemovePhoto={() => handleRemovePhoto(photo.id)}
              onVirtualStaging={onVirtualStaging}
              onCustomEdit={onCustomEdit}
              isGeneratingAI={generatingAI[photo.id] || false}
            />
          ))}
      </PhotoContainer>

      {aiPreviews.length > 0 && (
        <OpenAIPreviewsButton onClick={() => setIsAIPreviewModalOpen(true)}>
          🖼️ Ver {aiPreviews.length} preview
          {aiPreviews.length > 1 ? "s" : ""} de IA
        </OpenAIPreviewsButton>
      )}

      {isAIPreviewModalOpen && (
        <AIPreviewModalOverlay onClick={() => setIsAIPreviewModalOpen(false)}>
          <AIPreviewModalContent onClick={(e) => e.stopPropagation()}>
            <AIPreviewModalHeader>
              <AIPreviewModalTitle>
                Pré-visualizações geradas por IA ({aiPreviews.length})
              </AIPreviewModalTitle>
              <CloseModalButton onClick={() => setIsAIPreviewModalOpen(false)}>
                ×
              </CloseModalButton>
            </AIPreviewModalHeader>

            <AIPreviewGrid>
              {aiPreviews.map((preview) => {
                const original = photos.find((p) => p.id === preview.photo_id);
                const fallbackOriginalUrl = original?.url_m || original?.url_l;

                return (
                  <PhotoAiPreviewCompareCard
                    key={preview.id}
                    preview={preview}
                    fallbackOriginalUrl={fallbackOriginalUrl}
                    onApply={handleApplyAI}
                    onDiscard={handleDiscardAI}
                    onTryAgain={handleTryAgainAI}
                    variant="modal"
                  />
                );
              })}
            </AIPreviewGrid>
          </AIPreviewModalContent>
        </AIPreviewModalOverlay>
      )}

      <ButtonsWrapper className="mt-8 flex-wrap justify-center gap-2 sm:gap-3">
        {isDraft ? (
          <>
            <NextButton
              type="button"
              className="inline-flex gap-1.5 [&>svg]:!ml-0"
              onClick={() => {
                setFormOptionSelected("characteristic");
                window.scrollTo(0, 0);
              }}
            >
              <FiArrowLeft size={18} color="#FFF" aria-hidden />
              <p>Voltar</p>
            </NextButton>
            <RegisterButton
              type="button"
              onClick={!!loadingRequest ? () => {} : handleChangeOrders}
            >
              <p>{!!loadingRequest ? "Carregando..." : "REORDENAR"}</p>
            </RegisterButton>
            <SaveDraftButton
              isLoading={isSavingDraft}
              onClick={handleSavePhotosDraft}
            >
              Salvar rascunho
            </SaveDraftButton>
            <RegisterButton
              type="button"
              onClick={
                !!loadingRequest || !handlePublishDraft
                  ? () => {}
                  : handlePublishDraft
              }
            >
              <p>{!!loadingRequest ? "Publicando..." : "Publicar"}</p>
            </RegisterButton>
          </>
        ) : (
          <>
            <NextButton
              type="button"
              className="inline-flex gap-1.5 [&>svg]:!ml-0"
              onClick={() => {
                setFormOptionSelected("characteristic");
                window.scrollTo(0, 0);
              }}
            >
              <FiArrowLeft size={18} color="#FFF" aria-hidden />
              <p>Voltar</p>
            </NextButton>

            <RegisterButton
              type="button"
              onClick={!!loadingRequest ? () => {} : handleChangeOrders}
            >
              <p>{!!loadingRequest ? "Carregando..." : "REORDENAR"}</p>
            </RegisterButton>
          </>
        )}
      </ButtonsWrapper>
    </form>
  );
}
